import { Box, Button, Paper, TextField, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import * as yup from 'yup'
import { useAppSelector } from '../../../app/hooks'
import { useCreateVerifiedDomainMutation } from '../../../app/redux-fetch/apiQuery'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import messages from './messages'
import { CreateVerifiedDomainResponse, VerifiedDomainScope } from './types'

// Mirrors the handler regex: a valid FQDN, case-insensitive (the server lowercases on store).
const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i

interface CreateVerifiedDomainModalProps {
  onDone: () => void
}

// Scope is derived from the logged-in context in Phase 1: a Customer user verifies a Customer-scoped
// domain, a Tenant user a Tenant-scoped one. (A tenant-admin adding a domain on behalf of a specific
// Customer needs a customer picker - a follow-up once E8 lands the persona resolver.)
const CreateVerifiedDomainModal = ({ onDone }: CreateVerifiedDomainModalProps) => {
  const { formatMessage } = useIntl()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const { tenantId, customerId } = loggedInUser
  const [createVerifiedDomain] = useCreateVerifiedDomainMutation()
  const [created, setCreated] = useState<CreateVerifiedDomainResponse | undefined>()
  const [copied, setCopied] = useState(false)

  const schema = yup.object({
    domainName: yup
      .string()
      .required(formatMessage(messages.domainNameRequired))
      .matches(DOMAIN_RE, formatMessage(messages.domainNameRequired)),
  })

  // After creation, swap to the TXT-record reveal (the only time the token is shown).
  if (created) {
    return (
      <Box sx={{ m: '32px' }}>
        <Typography sx={{ mb: 2 }}>{formatMessage(messages.tokenInstructions)}</Typography>
        <Paper variant='outlined' sx={{ p: 2, fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {created.dnsRecordExample}
        </Paper>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant='outlined'
            onClick={() => {
              void navigator.clipboard.writeText(created.dnsRecordExample)
              setCopied(true)
            }}
          >
            {copied ? formatMessage(messages.copied) : formatMessage(messages.copy)}
          </Button>
          <Button variant='contained' onClick={onDone}>
            {formatMessage(messages.doneVerifyLater)}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Formik
      initialValues={{ domainName: '' }}
      validationSchema={schema}
      onSubmit={async (values) => {
        const scope = customerId ? VerifiedDomainScope.Customer : VerifiedDomainScope.Tenant
        const res = await createVerifiedDomain({
          tenantId,
          body: { domainName: values.domainName, scope, customerId: customerId || undefined },
        })
        if ('data' in res && res.data) {
          enqueueSnackbar(formatMessage(messages.domainCreated), { variant: 'success' })
          setCreated(res.data)
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
        <Form>
          <Box sx={{ m: '32px' }}>
            <TextField
              fullWidth
              name='domainName'
              label={formatMessage(messages.domainName)}
              placeholder='acme.com'
              value={values.domainName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.domainName && Boolean(errors.domainName)}
              helperText={touched.domainName && errors.domainName}
            />
            <Box sx={{ mt: 3 }}>
              <Button type='submit' variant='contained' disabled={isSubmitting}>
                {formatMessage(messages.addDomain)}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  )
}

export default CreateVerifiedDomainModal
